'use server';

/**
 * @fileOverview Generates a human-readable summary of the current weather conditions at the SRAC flying field.
 *
 * - liveWeatherSummary - A function that retrieves weather data and generates a summary.
 * - LiveWeatherSummaryInput - The input type for the liveWeatherSummary function (currently empty).
 * - LiveWeatherSummaryOutput - The return type for the liveWeatherSummary function.
 * 
 * Weather data sources:
 * - Primary: Weather Underground API (KWASNOHO499 station) - current conditions only
 * - Secondary: NOAA NWS API - 5-day forecast and fallback for current conditions
 * - Caching: Firebase Firestore (5-minute cache for WU data)
 */

import { z } from 'zod';

import { adminFirestore } from '@/firebase/admin';

// Helper to get Firestore instance
async function getAdminFirestore() {
  console.log('[Weather] Getting admin firestore instance...');
  if (!adminFirestore) {
    console.error('[Weather] adminFirestore is undefined!');
    throw new Error('Firebase Admin SDK not initialized');
  }
  return adminFirestore;
}

const LiveWeatherSummaryInputSchema = z.object({});
export type LiveWeatherSummaryInput = z.infer<
  typeof LiveWeatherSummaryInputSchema
>;

const WeatherDataSchema = z.object({
  temperature: z.number().describe('The current temperature in Fahrenheit.'),
  windSpeed: z.number().describe('The current wind speed in mph.'),
  windDirection: z
    .string()
    .describe('The current wind direction (e.g., N, S, E, W, NW, SE).'),
  humidity: z.number().describe('The current humidity percentage.'),
  visibility: z.number().describe('The current visibility in miles.'),
  rain: z.boolean().describe('Whether it is currently raining.'),
  summary: z
    .string()
    .describe(
      'A very short text summary of conditions (e.g., "Partly Cloudy").'
    ),
});

const ForecastDaySchema = z.object({
  name: z.string(),
  highTemp: z.number(),
  lowTemp: z.number(),
  shortForecast: z.string(),
  detailedForecast: z.string(),
  windSpeed: z.string(),
  windDirection: z.string(),
  precipitationChance: z.number().nullable(),
  icon: z.string(),
});

export type ForecastDay = z.infer<typeof ForecastDaySchema>;

const LiveWeatherSummaryOutputSchema = WeatherDataSchema.extend({
  detailedSummary: z
    .string()
    .describe(
      'A short, human-readable summary of the current weather conditions, interpreted for an RC pilot.'
    ),
  forecast: z.array(ForecastDaySchema),
});
export type LiveWeatherSummaryOutput = z.infer<
  typeof LiveWeatherSummaryOutputSchema
>;

// Cache configuration
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const CACHE_COLLECTION = 'weatherCache';
const CACHE_DOC_ID = 'currentWeather';

// Helper function to convert degrees to cardinal direction
function degreesToCardinal(deg: number): string {
  const cardinals = [
    'N',
    'NNE',
    'NE',
    'ENE',
    'E',
    'ESE',
    'SE',
    'SSE',
    'S',
    'SSW',
    'SW',
    'WSW',
    'W',
    'WNW',
    'NW',
    'NNW',
  ];
  const index = Math.round(deg / 22.5) % 16;
  return cardinals[index];
}

/**
 * Fetch current weather from Weather Underground API
 */
async function getWeatherUndergroundData(): Promise<z.infer<typeof WeatherDataSchema> | null> {
  const WU_API_KEY = process.env.WEATHER_UNDERGROUND_API_KEY || 'da661ac77f954280a61ac77f95328038';
  const WU_STATION_ID = process.env.WEATHER_UNDERGROUND_STATION_ID || 'KWASNOHO499';

  const apiUrl = `https://api.weather.com/v2/pws/observations/current?stationId=${WU_STATION_ID}&format=json&units=e&apiKey=${WU_API_KEY}`;

  try {
    console.log('[Weather] Fetching from Weather Underground API:', WU_STATION_ID);
    const response = await fetch(apiUrl, {
      cache: 'no-store',
    });

    if (!response.ok) {
      console.error(`[Weather] Weather Underground API failed with status: ${response.status}`);
      console.error(`[Weather] Response status text: ${response.statusText}`);
      const errorText = await response.text().catch(() => 'Could not read error response');
      console.error(`[Weather] Error response body:`, errorText);
      return null;
    }

    const data = await response.json();
    console.log('[Weather] Weather Underground API response received');
    const obs = data.observations?.[0];

    if (!obs) {
      console.error('[Weather] No observation data in Weather Underground response');
      console.error('[Weather] Response structure:', JSON.stringify(data, null, 2));
      return null;
    }

    console.log('[Weather] Weather Underground data parsed successfully');

    // Extract data from WU API response
    const temperature = obs.imperial?.temp || 0;
    const windSpeed = obs.imperial?.windSpeed || 0;
    const windGust = obs.imperial?.windGust || 0;
    const windDirection = obs.winddir ? degreesToCardinal(obs.winddir) : 'N/A';
    const humidity = obs.humidity || 0;

    // WU doesn't always provide visibility, estimate based on conditions
    const visibility = 10; // Default to 10 miles

    // Determine if it's raining based on precipitation rate
    const precipRate = obs.imperial?.precipRate || 0;
    const rain = precipRate > 0;

    // Create a summary based on conditions
    let summary = 'Clear';
    if (rain) {
      summary = 'Rain';
    } else if (obs.solarRadiation > 800) {
      summary = 'Sunny';
    } else if (obs.solarRadiation > 400) {
      summary = 'Partly Cloudy';
    } else if (obs.solarRadiation > 100) {
      summary = 'Mostly Cloudy';
    } else {
      summary = 'Cloudy';
    }

    return {
      temperature: Math.round(temperature),
      windSpeed: Math.round(windSpeed),
      windDirection,
      humidity: Math.round(humidity),
      visibility,
      rain,
      summary,
    };
  } catch (error) {
    console.error('[Weather] Error fetching Weather Underground data:', error);
    console.error('[Weather] Error details:', error instanceof Error ? error.message : String(error));
    return null;
  }
}

/**
 * Check Firebase cache for recent weather data
 */
async function getCachedWeatherData(): Promise<z.infer<typeof WeatherDataSchema> | null> {
  try {
    console.log('[Weather] Checking cache...');

    const db = await getAdminFirestore();
    const cacheDoc = await db.collection(CACHE_COLLECTION).doc(CACHE_DOC_ID).get();

    if (!cacheDoc.exists) {
      console.log('[Weather] No cache document found');
      return null;
    }

    const cacheData = cacheDoc.data();
    if (!cacheData) {
      console.log('[Weather] Cache document exists but has no data');
      return null;
    }

    // Check if cache is still fresh (less than 5 minutes old)
    const cacheTimestamp = cacheData.timestamp?.toMillis() || 0;
    const now = Date.now();
    const age = Math.floor((now - cacheTimestamp) / 1000);

    if (now - cacheTimestamp > CACHE_DURATION_MS) {
      console.log(`[Weather] Cache expired (age: ${age} seconds)`);
      return null;
    }

    console.log(`[Weather] Using cached weather data (age: ${age} seconds)`);
    return cacheData.data as z.infer<typeof WeatherDataSchema>;
  } catch (error) {
    console.error('[Weather] Error reading weather cache:', error);
    console.error('[Weather] Error details:', error instanceof Error ? error.message : String(error));
    return null;
  }
}

/**
 * Store weather data in Firebase cache
 */
async function cacheWeatherData(data: z.infer<typeof WeatherDataSchema>): Promise<void> {
  try {
    console.log('[Weather] Caching weather data...');
    const db = await getAdminFirestore();
    await db.collection(CACHE_COLLECTION).doc(CACHE_DOC_ID).set({
      data,
      timestamp: new Date(),
      source: 'weather-underground',
    });
    console.log('[Weather] Weather data cached successfully');
  } catch (error) {
    console.error('[Weather] Error caching weather data:', error);
    console.error('[Weather] Error details:', error instanceof Error ? error.message : String(error));
  }
}

/**
 * Fetch current weather from NOAA NWS API (fallback)
 */
async function getNOAACurrentData(): Promise<z.infer<typeof WeatherDataSchema>> {
  // Lat/Lon for Snohomish, WA 98290
  const lat = 47.91;
  const lon = -122.09;

  const headers = {
    'User-Agent': '(SRAC Website, srac@doug.tech)',
    Accept: 'application/geo+json',
  };

  // 1. Get the gridpoint for the lat/lon to find nearby stations
  const pointsUrl = `https://api.weather.gov/points/${lat},${lon}`;
  const pointsResponse = await fetch(pointsUrl, {
    headers,
    cache: 'no-store',
  });
  if (!pointsResponse.ok) {
    throw new Error(
      `Failed to fetch NWS points data: ${pointsResponse.status}`
    );
  }
  const pointsData = await pointsResponse.json();
  const stationsUrl = pointsData.properties.observationStations;

  // 2. Get the list of observation stations to find the closest one
  const stationsResponse = await fetch(stationsUrl, {
    headers,
    cache: 'no-store',
  });
  if (!stationsResponse.ok) {
    throw new Error(
      `Failed to fetch NWS stations: ${stationsResponse.status}`
    );
  }
  const stationsData = await stationsResponse.json();
  const stationId = stationsData.features[0]?.properties?.stationIdentifier;

  if (!stationId) {
    throw new Error('Could not find a valid weather station.');
  }

  // 3. Get the latest observation from the closest station
  const observationsUrl = `https://api.weather.gov/stations/${stationId}/observations/latest`;
  const obsResponse = await fetch(observationsUrl, {
    headers,
    cache: 'no-store',
  });

  if (!obsResponse.ok) {
    throw new Error('Failed to fetch weather data from NWS API.');
  }

  const obsData = await obsResponse.json();
  const props = obsData.properties;

  if (!props) {
    throw new Error('No weather properties found in NWS response.');
  }

  // Process current conditions
  // NWS provides data in metric. Convert to imperial.
  const tempC = props.temperature.value;
  const windSpeedMs = props.windSpeed.value;
  const visibilityM = props.visibility.value;
  const humidity = props.relativeHumidity.value;

  const tempF = tempC !== null ? Math.round((tempC * 9) / 5 + 32) : 0;
  const windSpeedMph =
    windSpeedMs !== null ? Math.round(windSpeedMs * 2.237) : 0;
  const visibilityMi =
    visibilityM !== null ? Math.round(visibilityM / 1609) : 0;

  const windDirection =
    props.windDirection.value !== null
      ? degreesToCardinal(props.windDirection.value)
      : 'N/A';

  const isRaining = props.presentWeather.some(
    (weather: { rawString: string }) => /RA/.test(weather.rawString)
  );

  return {
    temperature: tempF,
    windSpeed: windSpeedMph,
    windDirection: windDirection,
    humidity: Math.round(humidity),
    visibility: visibilityMi,
    rain: isRaining,
    summary: props.textDescription || 'N/A',
  };
}

/**
 * Fetch 5-day forecast from NOAA NWS API
 */
async function getNOAAForecastData(): Promise<ForecastDay[]> {
  // Lat/Lon for Snohomish, WA 98290
  const lat = 47.91;
  const lon = -122.09;

  const headers = {
    'User-Agent': '(SRAC Website, srac@doug.tech)',
    Accept: 'application/geo+json',
  };

  try {
    // 1. Get the forecast URL
    const pointsUrl = `https://api.weather.gov/points/${lat},${lon}`;
    const pointsResponse = await fetch(pointsUrl, {
      headers,
      cache: 'no-store',
    });
    if (!pointsResponse.ok) {
      throw new Error(
        `Failed to fetch NWS points data: ${pointsResponse.status}`
      );
    }
    const pointsData = await pointsResponse.json();
    const forecastUrl = pointsData.properties.forecast;

    // 2. Get the forecast
    const forecastResponse = await fetch(forecastUrl, {
      headers,
      cache: 'no-store',
    });
    if (!forecastResponse.ok) {
      throw new Error(
        `Failed to fetch NWS forecast data: ${forecastResponse.status}`
      );
    }
    const forecastData = await forecastResponse.json();
    const forecastPeriods = forecastData.properties.periods;

    // Process forecast data to get daily highs/lows
    const dailyForecasts: { [key: string]: Partial<ForecastDay> & { temps: number[], dayPeriod?: any, nightPeriod?: any } } = {};

    forecastPeriods.forEach((period: any) => {
      // Use startTime to group by calendar day
      const dayKey = new Date(period.startTime).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });

      if (!dailyForecasts[dayKey]) {
        dailyForecasts[dayKey] = { name: period.name.startsWith('Today') || period.name.startsWith('Tonight') ? period.name.split(' ')[0] : new Date(period.startTime).toLocaleDateString('en-US', { weekday: 'short' }), temps: [] };
      }

      dailyForecasts[dayKey].temps.push(period.temperature);

      if (period.isDaytime) {
        dailyForecasts[dayKey].dayPeriod = period;
      } else {
        dailyForecasts[dayKey].nightPeriod = period;
      }
    });

    const processedForecast = Object.values(dailyForecasts)
      .map(day => {
        const highTemp = Math.max(...day.temps);
        const lowTemp = Math.min(...day.temps);
        const representativePeriod = day.dayPeriod || day.nightPeriod;

        return {
          name: day.name || 'Day',
          highTemp,
          lowTemp,
          shortForecast: representativePeriod.shortForecast,
          detailedForecast: representativePeriod.detailedForecast,
          windSpeed: representativePeriod.windSpeed,
          windDirection: representativePeriod.windDirection,
          precipitationChance: representativePeriod.probabilityOfPrecipitation?.value,
          icon: representativePeriod.icon,
        } as ForecastDay;
      })
      .slice(0, 5);

    return processedForecast;
  } catch (error) {
    console.error('Error fetching NOAA forecast:', error);
    // Return fallback forecast data
    return [
      { name: 'Today', highTemp: 72, lowTemp: 50, shortForecast: 'Sunny', icon: '', detailedForecast: '', windDirection: 'NW', windSpeed: '5 mph', precipitationChance: 0 },
      { name: 'Tues', highTemp: 74, lowTemp: 52, shortForecast: 'Sunny', icon: '', detailedForecast: '', windDirection: 'NW', windSpeed: '5 mph', precipitationChance: 0 },
      { name: 'Wed', highTemp: 70, lowTemp: 48, shortForecast: 'Partly Cloudy', icon: '', detailedForecast: '', windDirection: 'NW', windSpeed: '5 mph', precipitationChance: 0 },
      { name: 'Thurs', highTemp: 68, lowTemp: 47, shortForecast: 'Showers', icon: '', detailedForecast: '', windDirection: 'NW', windSpeed: '5 mph', precipitationChance: 0 },
      { name: 'Fri', highTemp: 65, lowTemp: 45, shortForecast: 'Rain', icon: '', detailedForecast: '', windDirection: 'NW', windSpeed: '5 mph', precipitationChance: 0 },
    ];
  }
}

/**
 * Get current weather data with caching and fallback logic
 */
async function getCurrentWeatherData(): Promise<z.infer<typeof WeatherDataSchema>> {
  console.log('[Weather] getCurrentWeatherData called');

  // 1. Check cache first
  const cachedData = await getCachedWeatherData();
  if (cachedData) {
    return cachedData;
  }

  // 2. Try Weather Underground
  console.log('[Weather] Fetching fresh weather data from Weather Underground...');
  const wuData = await getWeatherUndergroundData();
  if (wuData) {
    // Cache the data
    await cacheWeatherData(wuData);
    return wuData;
  }

  // 3. Fallback to NOAA
  console.log('[Weather] Weather Underground failed, falling back to NOAA...');
  try {
    const noaaData = await getNOAACurrentData();
    console.log('[Weather] NOAA data retrieved successfully');
    // Cache NOAA data too
    await cacheWeatherData(noaaData);
    return noaaData;
  } catch (error) {
    console.error('[Weather] Both Weather Underground and NOAA failed:', error);
    console.error('[Weather] Error details:', error instanceof Error ? error.message : String(error));
    console.log('[Weather] Returning fallback data');
    // Return fallback data
    return {
      temperature: 68,
      windSpeed: 5,
      windDirection: 'NW',
      humidity: 55,
      visibility: 10,
      rain: false,
      summary: 'Clear (Fallback)',
    };
  }
}

export async function liveWeatherSummary(
  input: LiveWeatherSummaryInput
): Promise<LiveWeatherSummaryOutput> {
  console.log('[Weather] liveWeatherSummary called');

  try {
    console.log('[Weather] Fetching current weather data...');
    const current = await getCurrentWeatherData();
    console.log('[Weather] Current weather data retrieved:', current);

    console.log('[Weather] Fetching forecast data...');
    const forecast = await getNOAAForecastData();
    console.log('[Weather] Forecast data retrieved:', forecast.length, 'days');

    // Manually construct the detailed summary string from contextual info
    const summaryParts: string[] = [];
    if (current.rain) {
      summaryParts.push('It is currently raining.');
    }
    if (current.windSpeed > 15) {
      summaryParts.push('High winds may make flying difficult.');
    } else if (current.windSpeed > 10) {
      summaryParts.push('Winds are a bit breezy.');
    }

    const detailedSummary = summaryParts.join(' ');

    const result = {
      ...current,
      detailedSummary: detailedSummary,
      forecast: forecast,
    };

    console.log('[Weather] Successfully generated weather summary');
    return result;
  } catch (error) {
    console.error('[Weather] FATAL ERROR in liveWeatherSummary:', error);
    console.error('[Weather] Error stack:', error instanceof Error ? error.stack : 'No stack trace');

    // Re-throw the error so it's visible to the caller
    throw error;
  }
}
